/**
 * Trust Score Display - Fetches score from API and rotates the needle
 * Based on the Unity TrustScoreIndicator pattern
 */

export default function initScoreDisplay() {
	const needleElement = document.getElementById('trust-needle');
	const scoreValueElement = document.getElementById('trust-score-value');
	const statusElement = document.getElementById('trust-score-status');

	// Check if we're on the score display page
	if (!needleElement || !window.trustScoreConfig) {
		return;
	}

	const config = {
		apiUrl: window.trustScoreConfig.apiUrl,
		sessionsApiUrl: window.trustScoreConfig.sessionsApiUrl,
		createSessionApiUrl: window.trustScoreConfig.createSessionApiUrl,
		updateInterval: window.trustScoreConfig.updateInterval || 10000,
		minRotationAngle: 90, // Minimum rotation (score = 0)
		maxRotationAngle: -90,  // Maximum rotation (score = 1)
		rotationDuration: 1500 // Animation duration in ms
	};

	let currentScore = 0;
	let isRotating = false;
	let updateIntervalId = null;
	let sessionsUpdateIntervalId = null;

	// Get weights from config or use default fallback values
	let weights = {
		recent: window.trustScoreConfig.weights?.recent || 1.5,
		normal: window.trustScoreConfig.weights?.normal || 1.0,
		old: window.trustScoreConfig.weights?.old || 0.5
	};

	/**
	 * Fetch the current score from the API
	 */
	async function fetchScore() {
		try {
			statusElement.textContent = 'Updating...';

			const response = await fetch(config.apiUrl);

			if (!response.ok) {
				throw new Error(`API request failed: ${response.status}`);
			}

			const data = await response.json();

			if (data.status === 'success' && typeof data.score === 'number') {
				// Clamp score between 0 and 1
				const score = Math.max(0, Math.min(1, data.score));
				currentScore = score;

				// Update display
				scoreValueElement.textContent = score.toFixed(2);
				statusElement.textContent = 'Connected';

				// Update needle rotation
				updateNeedleRotation(score);
			} else {
				throw new Error('Invalid response format');
			}
		} catch (error) {
			console.error('Failed to fetch trust score:', error);
			statusElement.textContent = 'Error: Could not fetch score';
		}
	}

	/**
	 * Update the needle rotation based on score
	 * @param {number} score - Score value between 0 and 1
	 */
	function updateNeedleRotation(score) {
		// Map score (0 to 1) to rotation angle (minRotation to maxRotation)
		const targetAngle = config.minRotationAngle +
			(score * (config.maxRotationAngle - config.minRotationAngle));

		// Animate to target angle
		animateRotation(targetAngle);
	}

	/**
	 * Smoothly animate the needle to target angle
	 * @param {number} targetAngle - Target rotation angle in degrees
	 */
	function animateRotation(targetAngle) {
		if (isRotating) {
			return; // Don't start new animation if already rotating
		}

		isRotating = true;

		// Get current rotation
		const currentTransform = window.getComputedStyle(needleElement).transform;
		let currentAngle = 0;

		if (currentTransform && currentTransform !== 'none') {
			// Extract rotation from matrix
			const values = currentTransform.split('(')[1].split(')')[0].split(',');
			const a = parseFloat(values[0]);
			const b = parseFloat(values[1]);
			currentAngle = Math.round(Math.atan2(b, a) * (180 / Math.PI));
		}

		const startAngle = currentAngle;
		const startTime = performance.now();

		function animate(currentTime) {
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / config.rotationDuration, 1);

			// Use easeInOutCubic for smooth animation
			const easedProgress = progress < 0.5
				? 4 * progress * progress * progress
				: 1 - Math.pow(-2 * progress + 2, 3) / 2;

			// Interpolate angle
			const angle = startAngle + (targetAngle - startAngle) * easedProgress;

			// Apply rotation (rotate from bottom center)
			needleElement.style.transform = `rotate(${angle}deg)`;

			if (progress < 1) {
				requestAnimationFrame(animate);
			} else {
				isRotating = false;
			}
		}

		requestAnimationFrame(animate);
	}

	/**
	 * Fetch and display game sessions
	 */
	async function fetchGameSessions() {
		try {
			const response = await fetch(config.sessionsApiUrl);

			if (!response.ok) {
				throw new Error(`API request failed: ${response.status}`);
			}

			const data = await response.json();

			if (data.status === 'success') {
				const olderSummary = data.sessions.older_than_7_summary || {
					total: data.sessions.older_than_7.length,
					correct: data.sessions.older_than_7.filter(s => s.identified).length,
					incorrect: data.sessions.older_than_7.filter(s => !s.identified).length
				};

				// Calculate overall max possible score across all groups using configured weights
				const score24h = calculateGroupScore(data.sessions.last_24h, weights.recent);
				const score2_7d = calculateGroupScore(data.sessions.days_2_to_7, weights.normal);
				const scoreOlder = calculateGroupScoreFromCounts(olderSummary.total, olderSummary.correct, weights.old);
				const overallMaxPossibleScore = score24h.maxPossibleScore + score2_7d.maxPossibleScore + scoreOlder.maxPossibleScore;

				displaySessions('sessions-24h', 'count-24h', data.sessions.last_24h, overallMaxPossibleScore);
				displaySessions('sessions-2-7d', 'count-2-7d', data.sessions.days_2_to_7, overallMaxPossibleScore);
				displayOlderSummary('sessions-older', 'count-older', olderSummary, overallMaxPossibleScore);
			}
		} catch (error) {
			console.error('Failed to fetch game sessions:', error);
		}
	}

	/**
	 * Calculate score contribution for a group of sessions
	 * @param {Array} sessions - Array of session objects
	 * @param {number} weight - Weight multiplier for this age group
	 * @returns {Object} - Score statistics
	 */
	function calculateGroupScore(sessions, weight) {
		let totalScore = 0;
		let maxPossibleScore = 0;

		sessions.forEach(session => {
			if (session.identified) {
				totalScore += weight;
			}
			maxPossibleScore += weight;
		});

		// We're not removing negative numbers here because we want to see what each group contributes

		return {
			totalScore,
			maxPossibleScore,
			normalizedScore: maxPossibleScore > 0 ? totalScore / maxPossibleScore : 0
		};
	}

	/**
	 * Calculate score contribution from aggregate counts for a session group.
	 * @param {number} totalCount - Total number of sessions in group
	 * @param {number} correctCount - Number of correctly identified sessions in group
	 * @param {number} weight - Weight multiplier for this age group
	 * @returns {Object} - Score statistics
	 */
	function calculateGroupScoreFromCounts(totalCount, correctCount, weight) {
		const totalScore = correctCount * weight;
		const maxPossibleScore = totalCount * weight;

		return {
			totalScore,
			maxPossibleScore,
			normalizedScore: maxPossibleScore > 0 ? totalScore / maxPossibleScore : 0
		};
	}

	/**
	 * Display sessions in a grid
	 * @param {string} containerId - ID of the container element
	 * @param {string} countId - ID of the count element
	 * @param {Array} sessions - Array of session objects
	 * @param {number} overallMaxPossibleScore - Total max possible score across all groups
	 */
	function displaySessions(containerId, countId, sessions, overallMaxPossibleScore) {
		const container = document.getElementById(containerId);
		const countElement = document.getElementById(countId);

		if (!container || !countElement) return;

		// Determine weight based on container ID using dynamic weights
		let weight = weights.normal;
		if (containerId === 'sessions-24h') {
			weight = weights.recent;
		} else if (containerId === 'sessions-2-7d') {
			weight = weights.normal;
		} else if (containerId === 'sessions-older') {
			weight = weights.old;
		}

		// Clear existing content
		container.innerHTML = '';

		if (sessions.length === 0) {
			container.innerHTML = '<p class="l-sessions-section__empty">No sessions</p>';
			countElement.innerHTML = '0 sessions';
			return;
		}

		// Create grid items
		sessions.forEach(session => {
			const item = document.createElement('div');
			item.className = 'l-session-item';
			item.classList.add(session.identified ? 'l-session-item--correct' : 'l-session-item--incorrect');
			item.title = `${session.artefact_id} - ${session.identified ? 'Correctly identified' : 'Incorrectly identified'} (${session.age_days} days ago)`;
			container.appendChild(item);
		});

		// Calculate score contribution
		const correctCount = sessions.filter(s => s.identified).length;
		const incorrectCount = sessions.length - correctCount;
		const scoreStats = calculateGroupScore(sessions, weight);

		// Update count with score contribution
		countElement.innerHTML = `
			${sessions.length} sessions (${correctCount} correct, ${incorrectCount} incorrect)<br>
			<span class="l-sessions-section__score">
				Score contribution: ${scoreStats.totalScore.toFixed(1)}/${overallMaxPossibleScore.toFixed(1)}
				(${(scoreStats.normalizedScore * 100).toFixed(1)}%)
			</span>
		`;
	}

	/**
	 * Display summary-only data for older session bucket.
	 * @param {string} containerId - ID of the container element
	 * @param {string} countId - ID of the count element
	 * @param {Object} summary - Aggregate summary {total, correct, incorrect}
	 * @param {number} overallMaxPossibleScore - Total max possible score across all groups
	 */
	function displayOlderSummary(containerId, countId, summary, overallMaxPossibleScore) {
		const container = document.getElementById(containerId);
		const countElement = document.getElementById(countId);

		if (!countElement) return;

		const total = summary.total || 0;
		const correct = summary.correct || 0;
		const incorrect = summary.incorrect || 0;

		if (container) {
			container.innerHTML = '';
		}

		const scoreStats = calculateGroupScoreFromCounts(total, correct, weights.old);

		countElement.innerHTML = `
			${total} sessions (${correct} correct, ${incorrect} incorrect)<br>
			<span class="l-sessions-section__score">
				Score contribution: ${scoreStats.totalScore.toFixed(1)}/${overallMaxPossibleScore.toFixed(1)}
				(${(scoreStats.normalizedScore * 100).toFixed(1)}%)
			</span>
		`;
	}

	/**
	 * Start periodic score updates
	 */
	function startUpdating() {
		// Initial fetch
		fetchScore();
		fetchGameSessions();

		// Set up periodic updates
		updateIntervalId = setInterval(fetchScore, config.updateInterval);
		sessionsUpdateIntervalId = setInterval(fetchGameSessions, config.updateInterval);
	}

	/**
	 * Stop periodic updates
	 */
	function stopUpdating() {
		if (updateIntervalId) {
			clearInterval(updateIntervalId);
			updateIntervalId = null;
		}
		if (sessionsUpdateIntervalId) {
			clearInterval(sessionsUpdateIntervalId);
			sessionsUpdateIntervalId = null;
		}
	}

	/**
	 * Submit bulk session creation form
	 * @param {FormData} formData - Form data to submit
	 */
	async function submitBulkSessionForm(formData) {
		const statusElement = document.getElementById('form-status');
		const submitButton = document.querySelector('#bulk-session-form button[type="submit"]');

		try {
			// Disable submit button
			submitButton.disabled = true;
			statusElement.textContent = 'Creating sessions...';
			statusElement.className = 'l-bulk-session-form__status';

			// Get form values
			const count = parseInt(formData.get('count'));
			const identified = formData.get('identified') === 'on';
			const artefactId = formData.get('artefact_id');
			const apiKey = formData.get('api_key');

			// Validate count
			if (count < 1 || count > 100) {
				throw new Error('Count must be between 1 and 100');
			}

			// Create sessions one by one
			let successCount = 0;
			let failCount = 0;

			for (let i = 0; i < count; i++) {
				try {
					// Update status with progress
					statusElement.textContent = `Creating session ${i + 1} of ${count}...`;

					const response = await fetch(config.createSessionApiUrl, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json'
						},
						body: JSON.stringify({
							key: apiKey,
							artefact_id: artefactId,
							identified: identified
						})
					});

					if (!response.ok) {
						throw new Error(`API request failed: ${response.status}`);
					}

					const result = await response.json();

					if (result.status === 'success') {
						successCount++;
					} else {
						failCount++;
						console.error(`Failed to create session ${i + 1}:`, result);
					}
				} catch (error) {
					failCount++;
					console.error(`Error creating session ${i + 1}:`, error);
				}
			}

			// Show final status
			if (failCount === 0) {
				statusElement.textContent = `Successfully created all ${successCount} sessions!`;
				statusElement.className = 'l-bulk-session-form__status l-bulk-session-form__status--success';
			} else if (successCount === 0) {
				statusElement.textContent = `Failed to create all ${count} sessions`;
				statusElement.className = 'l-bulk-session-form__status l-bulk-session-form__status--error';
			} else {
				statusElement.textContent = `Created ${successCount} sessions (${failCount} failed)`;
				statusElement.className = 'l-bulk-session-form__status l-bulk-session-form__status--success';
			}

			// Refresh the sessions display
			await fetchGameSessions();
			await fetchScore();

			// Clear success message after 3 seconds
			setTimeout(() => {
				statusElement.textContent = '';
				statusElement.className = 'l-bulk-session-form__status';
			}, 3000);

		} catch (error) {
			console.error('Failed to create bulk sessions:', error);
			statusElement.textContent = `Error: ${error.message}`;
			statusElement.className = 'l-bulk-session-form__status l-bulk-session-form__status--error';
		} finally {
			// Re-enable submit button
			submitButton.disabled = false;
		}
	}

	/**
	 * Initialize bulk session form
	 */
	function initBulkSessionForm() {
		const form = document.getElementById('bulk-session-form');
		if (!form) return;

		form.addEventListener('submit', async (e) => {
			e.preventDefault();
			const formData = new FormData(form);
			await submitBulkSessionForm(formData);
		});
	}

	// Initialize
	startUpdating();
	initBulkSessionForm();

	// Clean up on page unload
	window.addEventListener('beforeunload', stopUpdating);

	// Return public API
	return {
		fetchScore,
		fetchGameSessions,
		getCurrentScore: () => currentScore,
		startUpdating,
		stopUpdating,
		submitBulkSessionForm
	};
}
